import { Card, CardContent, Stack, Typography } from '@mui/joy';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { parseString } from 'core/utils';
import { QuoteEntityType, QuotePartNoteType } from 'quotes/enums';
import { ILeadTime, IQuotePartNoteForm } from 'quotes/types';
import { Color, Level, Variant } from 'theme/enums';

import { getNoteInfoTooltip, getNoteLabel, getNoteMaxLength } from './utils';

interface IProps {
  isOpen?: boolean;
  level?: number;
  onClose?: (reset?: UseFormReset<IQuotePartNoteForm>, closeSelection?: boolean) => void;
  onSubmit?: (reset?: UseFormReset<IQuotePartNoteForm>) => (data: IQuotePartNoteForm) => void;
  types: QuotePartNoteType[];
  leadTime?: ILeadTime;
  disableUndiscardableItem?: boolean;
  entityType?: QuoteEntityType;
  isAlternateVersionEnabled?: boolean;
}

export const QuotePartAddNoteForm = ({
  isOpen = false,
  level,
  onClose,
  onSubmit,
  types,
  leadTime,
  disableUndiscardableItem = false,
  entityType = QuoteEntityType.QUOTE,
  isAlternateVersionEnabled = false,
}: IProps) => {
  const { t } = useTranslation();

  const { watch, control, errors } = useFormContext();

  const noteFormFields = types.map((type) => {
    const label = getNoteLabel(type);
    const infoText = getNoteInfoTooltip(type);

    const isEnabled = watch(`notes.${type}.enabled`);

    return (
      <Card key={type} color={Color.NEUTRAL} variant={Variant.PLAIN}>
        <CardContent>
          <Stack gap={1}>
            <FormField
              key={`${type}-enabled`}
              name={`notes.${type}.enabled`}
              type={InputType.CHECKBOX}
              inputProps={{
                control,
                error: errors?.notes?.[type]?.enabled,
                label,
                disabled: disableUndiscardableItem && type === QuotePartNoteType.UNDISCARDABLE_ITEM,
                sx: { width: '100%' },
              }}
            />
            {infoText && (
              <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
                {infoText}
              </Typography>
            )}
            {isEnabled &&
              type === QuotePartNoteType.LEAD_TIME &&
              leadTime &&
              leadTime.leadTimeDescription && (
                <FormField
                  key={`${type}-lead-time`}
                  name={`notes.${type}.hasLeadTime`}
                  type={InputType.CHECKBOX}
                  inputProps={{
                    control,
                    error: errors?.notes?.[type]?.hasLeadTime,
                    label: (
                      <Stack>
                        {leadTime.leadTimeDescription.split('<br>').map((item, index) => (
                          <Typography key={index}>
                            {parseString(
                              item.replace(/(\d+ days?)/g, (match) => `<strong>${match}</strong>`)
                            )}
                          </Typography>
                        ))}
                      </Stack>
                    ),
                  }}
                />
              )}
            {isEnabled && type !== QuotePartNoteType.UNDISCARDABLE_ITEM && (
              <FormField
                key={`${type}-content`}
                name={`notes.${type}.content`}
                type={InputType.TEXT_EDITOR}
                inputProps={{
                  control,
                  error: errors?.notes?.[type]?.content,
                  height: 200,
                  maxLength:
                    isAlternateVersionEnabled && QuoteEntityType.ORDER === entityType
                      ? getNoteMaxLength(type)
                      : undefined,
                }}
              />
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  });

  return (
    <Form
      title={t('titleCreateNote')}
      level={level}
      isOpen={isOpen}
      onClose={onClose}
      submitBtnLabel={t('labelCreateNote')}
      onSubmit={onSubmit}
    >
      <>{noteFormFields}</>
    </Form>
  );
};
